import { defineStore } from "pinia";
import type { Business, BusinessBooking, Category, Employee, Location, Service, Calendar, DateTimeFormat, Country, Booking, PaymentMethod } from "~/types";

export const useBusinessStore = defineStore("businessStore", () => {
  const business = ref<null | Business>(null);
  const isMenuOpen = ref<boolean>(false);
  const isSearchOpen = ref<boolean>(false);
  const selectedLocation = ref<null | Location>(null);
  const backToServices = ref<boolean>(false);
  const backToTimes = ref<boolean>(false);
  const backToSummary = ref<boolean>(false);
  const backToProfile = ref<boolean>(false);
  const backToSignIn = ref<boolean>(false);
  const employees = ref<Employee[]>([]);
  const categories = ref<Category[]>([]);
  const availableCategories = ref<Category[]>([]);
  const booking = ref<BusinessBooking>({
    selectedServicesIds: [],
    bookedServices: [],
    subTotal: 0,
    date: business.value?.current_date || "",
    paymentMethodId: null,
    tapToken: "",
    summary: null,
  });
  const calendar = ref<Calendar | null>(null);
  const search = ref<string>('');
  const default_js_formats = ref<DateTimeFormat | null>(null);
  const countries = ref<Country[]>([]);
  const canRefreshStaff = ref<boolean>(true);
  const canRefreshServices = ref<boolean>(true);
  const goToCheckout = ref<boolean>(false);
  const cancelBookingOpen = ref<boolean>(false);
  const canceledBooking = ref<null | Booking>(null);
  const bookings = ref<Booking[]>([]);
  const selectedPaymentMethod = ref<null | PaymentMethod>(null);
  const creatingNewBooking = ref(false);
  const tapLoading = ref(false);
  const isCardValid = ref(false);
  const isNavigating = ref(false);

  const clearBooking = () => {
    booking.value.bookedServices = [];
    booking.value.subTotal = 0;
    booking.value.date = business.value?.current_date || "";
    booking.value.selectedServicesIds = [];
    booking.value.paymentMethodId = null;
    booking.value.tapToken = "";
    booking.value.summary = null;
    canRefreshServices.value = true;
  }

  const openMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
  }

  const openSearch = () => {
    isSearchOpen.value = !isSearchOpen.value
  }

  function searchServiceByName() {
    if (search.value === '') {
      availableCategories.value = categories.value;
      return;
    }
    const results = <typeof availableCategories.value>[];
    categories.value.forEach((category: Category) => {
      const matchingServices = category.services.filter((service: Service) => {
        return service.name && service.name.toLowerCase().includes(search.value.toLowerCase());
      });
      if (matchingServices.length > 0) {
        results.push({ ...category, services: matchingServices });
      }
    });
    availableCategories.value = results;
  }

  const clearSearch = () => {
    search.value = "";
    searchServiceByName();
  }

  watch(selectedLocation, (newVal, oldVal) => {
    if (newVal?.id !== oldVal?.id) {
      canRefreshServices.value = true;
      canRefreshStaff.value = true;
    }
  }, { deep: true });

  watch(booking, () => {
    booking.value.subTotal = booking.value.selectedServicesIds.map((id) => categories.value.flatMap((category) => category.services).find((service) => service.id === id))
      .reduce((acc, service) => acc + Number(service?.price), 0);
  }, { deep: true });

  function $reset() {
    business.value = null;
    isMenuOpen.value = false;
    isSearchOpen.value = false;
    selectedLocation.value = null;
    employees.value = [];
    clearBooking();
    backToServices.value = false;
    backToTimes.value = false;
    search.value = '';
    default_js_formats.value = null;
    canRefreshStaff.value = true;
    canRefreshServices.value = true;
    goToCheckout.value = false;
    backToSummary.value = false;
    cancelBookingOpen.value = false;
    canceledBooking.value = null;
    bookings.value = [];
    backToSignIn.value = false;
    selectedPaymentMethod.value = null;
    creatingNewBooking.value = false;
    tapLoading.value = false;
    isCardValid.value = false;
    isNavigating.value = false;
  }

  return {
    business,
    isMenuOpen,
    isSearchOpen,
    selectedLocation,
    backToServices,
    backToTimes,
    backToProfile,
    employees,
    categories,
    availableCategories,
    booking,
    calendar,
    search,
    default_js_formats,
    countries,
    canRefreshServices,
    canRefreshStaff,
    goToCheckout,
    backToSummary,
    cancelBookingOpen,
    canceledBooking,
    bookings,
    backToSignIn,
    selectedPaymentMethod,
    creatingNewBooking,
    tapLoading,
    isCardValid,
    isNavigating,
    openMenu,
    openSearch,
    clearBooking,
    searchServiceByName,
    clearSearch,
    $reset
  }
});
